import React, { useEffect, useRef, useState } from 'react';
import './App.css';

// ---- Config (prototype "Tweaks" props -> plain constants) ----
const GITHUB_USER = 'mauricioguevara';
const DIFFICULTY = 'classic'; // 'chill' | 'classic' | 'hard'
const TOASTS = true;
const PORTRAIT_SRC = '/images/profile.jpg';

const shineBg =
  'linear-gradient(115deg,rgba(255,255,255,.45),rgba(255,255,255,.06) 30%,rgba(255,255,255,.2) 50%,rgba(255,255,255,.06) 70%,rgba(255,255,255,.45))';
const shineBgStrong =
  'linear-gradient(115deg,rgba(255,255,255,.5),rgba(255,255,255,.08) 30%,rgba(255,255,255,.25) 50%,rgba(255,255,255,.08) 70%,rgba(255,255,255,.5))';

const mono = "'IBM Plex Mono',monospace";
const serif = "'Instrument Serif',serif";

const githubProfile = 'https://github.com/' + GITHUB_USER;

const cats = [
  {
    zone: '2-1',
    label: 'SOFTWARE',
    word: 'BUILDS',
    items: [
      {
        id: 'packet-lab',
        name: 'Packet Lab',
        year: '2026',
        desc: 'A full small-office network built in Cisco Packet Tracer — VLANs, routing, subnetting, and nothing left unlabeled.',
        tags: ['NETWORKING', 'PACKET TRACER'],
        github: githubProfile,
        video: '',
        brief:
          'Design and build a complete small-office network in Cisco Packet Tracer — every device placed, addressed, and documented like a real deployment.',
        steps: [
          'Mapped the office: departments, floors, and one server closet.',
          'Carved VLANs and subnets so each team gets its own lane.',
          'Configured inter-VLAN routing and ACLs, then labeled everything.',
          'Stress-tested with simulated traffic and documented the topology.',
        ],
        bugs: [
          {
            p: 'Two VLANs could reach each other when they should not.',
            f: 'Traced it to a trunk port carrying every VLAN — pruned the allowed list.',
          },
        ],
      },
      {
        id: 'fb-notes',
        name: 'Firebase Notes',
        year: '2026',
        desc: 'A realtime notes app on Firebase Cloud — auth, sync, and a UI that stays out of the way.',
        tags: ['JAVASCRIPT', 'CLOUD'],
        github: githubProfile,
        video: '',
        brief:
          'Build a notes app where two devices stay in sync in real time, with sign-in and zero manual refreshes.',
        steps: [
          'Set up Firebase Auth and Firestore with per-user collections.',
          'Wired realtime listeners so edits appear live everywhere.',
          'Kept the UI minimal — dark, fast, keyboard-friendly.',
        ],
        bugs: [
          {
            p: 'Notes flickered and duplicated on fast edits.',
            f: 'Local writes were racing the server echo — switched to latency-compensated snapshot handling.',
          },
        ],
      },
    ],
  },
  {
    zone: '2-2',
    label: 'HARDWARE',
    word: 'BUILDS',
    items: [
      {
        id: 'bench-pc',
        name: 'Bench PC Revival',
        year: '2025',
        desc: 'A dead tower brought back to life on the bench — diagnosis, part swaps, clean cable runs.',
        tags: ['HARDWARE', 'DIAGNOSTICS'],
        github: githubProfile,
        video: '',
        brief: 'Take one non-booting PC, isolate every fault, and rebuild it into a daily driver.',
        steps: [
          'Bench-tested PSU, RAM, and drives to isolate the failure.',
          'Swapped the dead PSU, reseated everything, mapped clean cable runs.',
          'Fresh OS install, drivers, and an overnight stress test.',
        ],
        bugs: [
          {
            p: 'Random shutdowns under load even after the new PSU.',
            f: 'Ancient thermal paste — repasted the CPU and it held steady.',
          },
        ],
      },
    ],
  },
  {
    zone: '2-3',
    label: 'ART',
    word: 'PIECES',
    items: [
      {
        id: 'art-01',
        name: 'Off the Clock',
        year: '2025',
        desc: 'Drawings and digital pieces — the eye behind the engineering. First drops coming soon.',
        tags: ['DIGITAL ART'],
        github: githubProfile,
        video: '',
        brief: 'The fun side of the portfolio — pieces go up here as they get finished.',
        steps: ['Sketch on paper, refine digitally.', 'Post the process, not just the result.'],
        bugs: [],
      },
    ],
  },
];

const groups = cats.map((c) => ({
  zone: c.zone,
  label: c.label,
  count: String(c.items.length).padStart(2, '0') + ' ' + c.word,
  projects: c.items.map((p, i) => ({ ...p, delay: i * 140 })),
}));

const skills = [
  ['JavaScript', false],
  ['Python', true],
  ['C++', false],
  ['Routing', true],
  ['Java', false],
  ['Subnetting', true],
  ['C#', false],
  ['Firebase Cloud', true],
  ['Packet Tracer', false],
  ['Operating Systems', true],
];

const GithubMark = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
  </svg>
);

const SkillMarqueeGroup = () => (
  <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
    {skills.map(([name, italic], i) => (
      <span
        key={i}
        style={{
          fontFamily: serif,
          fontStyle: italic ? 'italic' : 'normal',
          fontSize: 'clamp(30px,3.6vw,46px)',
          paddingRight: 44,
          color: italic ? 'rgba(242,242,240,.7)' : undefined,
        }}
      >
        {name}{' '}
        <span style={{ color: 'rgba(242,242,240,.35)', fontSize: '.62em' }}>✦</span>
      </span>
    ))}
  </div>
);

function App() {
  const stageRef = useRef(null);
  const toastRef = useRef(null);
  const logToastedRef = useRef(false);
  const [openLogs, setOpenLogs] = useState({});

  const toggleLog = (id) => {
    const willOpen = !openLogs[id];
    if (willOpen && !logToastedRef.current) {
      logToastedRef.current = true;
      if (toastRef.current) toastRef.current('ACHIEVEMENT ✦ LOG DECRYPTED');
    }
    setOpenLogs((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    const props = {
      difficulty: DIFFICULTY,
      motion: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'calm' : 'full',
      toasts: TOASTS,
    };

    const cleanups = [];
    const on = (target, ev, fn, opts) => {
      target.addEventListener(ev, fn, opts);
      cleanups.push(() => target.removeEventListener(ev, fn, opts));
    };

    const toast = stage.querySelector('[data-toast]');
    const toastsOn = () => props.toasts;
    const diffMult = () =>
      props.difficulty === 'chill' ? 0.75 : props.difficulty === 'hard' ? 1.35 : 1;

    const showToast = (text) => {
      const t = toast;
      if (!t || !text || !toastsOn()) return;
      clearTimeout(t._t);
      t.textContent = text;
      t.style.opacity = '1';
      t.style.transform = 'translateX(-50%) translateY(0)';
      t._t = setTimeout(() => {
        t.style.opacity = '0';
        t.style.transform = 'translateX(-50%) translateY(12px)';
      }, 2200);
    };
    cleanups.push(() => clearTimeout(toast && toast._t));
    toastRef.current = showToast;
    cleanups.push(() => {
      toastRef.current = null;
    });

    // ---- applyPrefs ----
    stage.querySelectorAll('[data-anim]').forEach((el) => {
      el.style.animationPlayState = props.motion === 'calm' ? 'paused' : 'running';
    });

    // ---- wireScroll ----
    const fill = stage.querySelector('[data-fill]');
    const pct = stage.querySelector('[data-pct]');
    const lvl = stage.querySelector('[data-lvl]');
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      if (fill) fill.style.width = (p * 100).toFixed(1) + '%';
      if (pct) pct.textContent = String(Math.round(p * 100)).padStart(2, '0') + '%';
      if (lvl) lvl.textContent = String(1 + Math.min(4, Math.floor(p * 5))).padStart(2, '0');
    };
    on(window, 'scroll', onScroll, { passive: true });
    onScroll();

    // ---- wireReveals ----
    const items = Array.from(stage.querySelectorAll('[data-reveal]'));
    items.forEach((el) => {
      if (el._base === undefined) el._base = el.style.transform || '';
      el.style.opacity = '0';
      el.style.transform = (el._base ? el._base + ' ' : '') + 'translateY(30px)';
      el.style.transition =
        'opacity .8s cubic-bezier(.22,.7,.25,1), transform .8s cubic-bezier(.22,.7,.25,1)';
    });
    const io = new IntersectionObserver(
      (ents) => {
        ents.forEach((en) => {
          if (!en.isIntersecting) return;
          const el = en.target;
          const d = parseInt(el.getAttribute('data-delay') || '0', 10);
          setTimeout(() => {
            el.style.opacity = '1';
            el.style.transform = el._base;
          }, d);
          const msg = el.getAttribute('data-toast-msg');
          if (msg) showToast(msg);
          io.unobserve(el);
        });
      },
      { threshold: 0.18 }
    );
    items.forEach((el) => io.observe(el));
    cleanups.push(() => io.disconnect());

    // ---- wireEggs ----
    const eggMsgs = [
      '+10 XP',
      '+25 XP — COMBO',
      'ACHIEVEMENT ✦ PERSISTENT',
      'OK, ONE MORE',
      'CHEAT CODE? TRY GAME MODE ▸',
    ];
    stage.querySelectorAll('[data-egg]').forEach((egg) => {
      let n = 0;
      egg.style.cursor = 'pointer';
      on(egg, 'click', () => {
        n++;
        egg.style.transition = 'transform .55s cubic-bezier(.34,1.56,.64,1)';
        egg.style.transform = 'rotate(' + n * 360 + 'deg) scale(1.14)';
        setTimeout(() => {
          egg.style.transform = 'rotate(' + n * 360 + 'deg) scale(1)';
        }, 560);
        showToast(eggMsgs[Math.min(n - 1, eggMsgs.length - 1)]);
      });
    });

    // ---- wirePortrait (chrome mode) ----
    let portraitRaf = 0;
    const wrap = stage.querySelector('[data-portrait]');
    if (wrap) {
      const frame = wrap.querySelector('[data-portrait-frame]');
      const media = wrap.querySelector('[data-portrait-media]');
      const canvas = wrap.querySelector('[data-chrome-canvas]');
      const hint = wrap.querySelector('[data-chrome-hint]');
      const ctx = canvas.getContext('2d');
      let hover = false;
      const t0 = performance.now();
      let mx = 0.5;
      let my = 0.35;

      const size = () => {
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        canvas.width = canvas.clientWidth * dpr;
        canvas.height = canvas.clientHeight * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };

      const draw = () => {
        if (!hover) return;
        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        const t = (performance.now() - t0) / 1000;
        ctx.clearRect(0, 0, w, h);
        ctx.globalCompositeOperation = 'screen';
        for (let i = 0; i < 5; i++) {
          const ph = t * (0.5 + i * 0.13) + i * 1.7;
          const cx = w * (0.5 + 0.55 * Math.sin(ph)) + (mx - 0.5) * w * 0.5;
          const gr = ctx.createLinearGradient(cx - w * 0.42, 0, cx + w * 0.42, h);
          gr.addColorStop(0, 'rgba(255,255,255,0)');
          gr.addColorStop(
            0.5,
            'rgba(255,255,255,' + (0.16 + 0.07 * Math.sin(ph * 1.3)).toFixed(3) + ')'
          );
          gr.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.fillStyle = gr;
          ctx.save();
          ctx.translate(w / 2, h / 2);
          ctx.rotate(-0.5 + 0.18 * Math.sin(t * 0.4 + i));
          ctx.translate(-w / 2, -h / 2);
          ctx.fillRect(-w * 0.3, -h * 0.3, w * 1.6, h * 1.6);
          ctx.restore();
        }
        const spec = ctx.createRadialGradient(mx * w, my * h, 0, mx * w, my * h, w * 0.5);
        spec.addColorStop(0, 'rgba(255,255,255,.30)');
        spec.addColorStop(0.4, 'rgba(255,255,255,.08)');
        spec.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = spec;
        ctx.fillRect(0, 0, w, h);
        portraitRaf = requestAnimationFrame(draw);
      };

      on(wrap, 'mouseenter', () => {
        hover = true;
        size();
        media.style.filter = 'grayscale(1) contrast(1.3) brightness(1.12)';
        canvas.style.opacity = '1';
        frame.style.transition = 'transform .5s cubic-bezier(.22,.7,.25,1)';
        if (hint) hint.textContent = '✦ CHROME MODE ACTIVE';
        cancelAnimationFrame(portraitRaf);
        portraitRaf = requestAnimationFrame(draw);
        if (!wrap._chromed) {
          wrap._chromed = true;
          showToast('ACHIEVEMENT ✦ SILVER SURFER');
        }
      });
      on(wrap, 'mousemove', (e) => {
        const r = frame.getBoundingClientRect();
        mx = (e.clientX - r.left) / r.width;
        my = (e.clientY - r.top) / r.height;
        if (props.motion !== 'calm') {
          frame.style.transform =
            'perspective(700px) rotateY(' +
            ((mx - 0.5) * 10).toFixed(2) +
            'deg) rotateX(' +
            ((0.5 - my) * 10).toFixed(2) +
            'deg)';
        }
      });
      on(wrap, 'mouseleave', () => {
        hover = false;
        cancelAnimationFrame(portraitRaf);
        media.style.filter = '';
        canvas.style.opacity = '0';
        frame.style.transform = '';
        if (hint) hint.textContent = 'HOVER — CHROME MODE';
      });
    }
    cleanups.push(() => cancelAnimationFrame(portraitRaf));

    // ---- wireGame (Asteroids) ----
    const g = {
      overlay: stage.querySelector('[data-game-overlay]'),
      canvas: stage.querySelector('[data-game-canvas]'),
      scoreEl: stage.querySelector('[data-gm-score]'),
      bestEl: stage.querySelector('[data-gm-best]'),
      livesEl: stage.querySelector('[data-gm-lives]'),
      startEl: stage.querySelector('[data-gm-start]'),
      overEl: stage.querySelector('[data-gm-over]'),
      finalEl: stage.querySelector('[data-gm-final]'),
      open: false,
      mode: 'idle',
      keys: {},
      raf: 0,
      last: 0,
      ship: null,
      bullets: [],
      rocks: [],
      score: 0,
      lives: 3,
      wave: 0,
      best: parseInt(localStorage.getItem('mg-asteroids-best') || '0', 10),
      stars: Array.from({ length: 110 }, () => ({
        x: Math.random(),
        y: Math.random(),
        r: Math.random() * 1.2 + 0.3,
        a: Math.random() * 0.3 + 0.08,
      })),
    };
    g.ctx = g.canvas.getContext('2d');
    g.bestEl.textContent = String(g.best).padStart(5, '0');

    const sizeCanvas = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      g.w = g.overlay.clientWidth;
      g.h = g.overlay.clientHeight;
      g.canvas.width = g.w * dpr;
      g.canvas.height = g.h * dpr;
      g.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const wrapObj = (o, r) => {
      if (o.x < -r) o.x = g.w + r;
      if (o.x > g.w + r) o.x = -r;
      if (o.y < -r) o.y = g.h + r;
      if (o.y > g.h + r) o.y = -r;
    };
    const makeRock = (x, y, size2) => {
      const m = diffMult();
      const r = size2 === 3 ? 46 : size2 === 2 ? 26 : 13;
      const sp = (size2 === 3 ? 55 : size2 === 2 ? 85 : 125) * m * (0.7 + Math.random() * 0.6);
      const ang = Math.random() * Math.PI * 2;
      return {
        x,
        y,
        size: size2,
        r,
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp,
        rot: Math.random() * Math.PI * 2,
        rs: (Math.random() - 0.5) * 1.6,
        verts: Array.from({ length: 10 }, () => 0.72 + Math.random() * 0.5),
      };
    };
    const spawnWave = (n) => {
      for (let i = 0; i < n; i++) {
        const edge = Math.random() < 0.5;
        const x = edge ? (Math.random() < 0.5 ? -50 : g.w + 50) : Math.random() * g.w;
        const y = edge ? Math.random() * g.h : Math.random() < 0.5 ? -50 : g.h + 50;
        g.rocks.push(makeRock(x, y, 3));
      }
    };
    const updHud = () => {
      g.scoreEl.textContent = String(g.score).padStart(5, '0');
      g.livesEl.textContent =
        g.lives > 0 ? Array.from({ length: g.lives }, () => '▲').join(' ') : '—';
    };
    const endRun = () => {
      g.mode = 'over';
      if (g.score > g.best) {
        g.best = g.score;
        localStorage.setItem('mg-asteroids-best', String(g.best));
      }
      g.bestEl.textContent = String(g.best).padStart(5, '0');
      g.finalEl.textContent =
        'SCORE ' + String(g.score).padStart(5, '0') + ' — BEST ' + String(g.best).padStart(5, '0');
      g.overEl.style.display = 'flex';
    };
    const updateGame = (dt) => {
      const s = g.ship;
      const k = g.keys;
      if (k['ArrowLeft'] || k['a']) s.a -= 4.2 * dt;
      if (k['ArrowRight'] || k['d']) s.a += 4.2 * dt;
      const thrust = k['ArrowUp'] || k['w'];
      if (thrust) {
        s.vx += Math.cos(s.a) * 300 * dt;
        s.vy += Math.sin(s.a) * 300 * dt;
      }
      s.thrust = thrust;
      const damp = 1 - 0.4 * dt;
      s.vx *= damp;
      s.vy *= damp;
      const spd = Math.hypot(s.vx, s.vy);
      if (spd > 440) {
        s.vx *= 440 / spd;
        s.vy *= 440 / spd;
      }
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      wrapObj(s, 14);
      s.cool -= dt;
      s.inv = Math.max(0, s.inv - dt);
      if (k[' '] && s.cool <= 0 && g.bullets.length < 4) {
        g.bullets.push({
          x: s.x + Math.cos(s.a) * 15,
          y: s.y + Math.sin(s.a) * 15,
          vx: Math.cos(s.a) * 540 + s.vx,
          vy: Math.sin(s.a) * 540 + s.vy,
          life: 0.95,
        });
        s.cool = 0.22;
      }
      g.bullets = g.bullets.filter((b) => (b.life -= dt) > 0);
      g.bullets.forEach((b) => {
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        wrapObj(b, 2);
      });
      g.rocks.forEach((r) => {
        r.x += r.vx * dt;
        r.y += r.vy * dt;
        r.rot += r.rs * dt;
        wrapObj(r, r.r);
      });

      const newRocks = [];
      g.rocks = g.rocks.filter((r) => {
        const hit = g.bullets.find((b) => Math.hypot(b.x - r.x, b.y - r.y) < r.r);
        if (!hit) return true;
        hit.life = 0;
        g.score += r.size === 3 ? 20 : r.size === 2 ? 50 : 100;
        if (r.size > 1) {
          newRocks.push(makeRock(r.x, r.y, r.size - 1), makeRock(r.x, r.y, r.size - 1));
        }
        return false;
      });
      g.bullets = g.bullets.filter((b) => b.life > 0);
      g.rocks.push(...newRocks);

      if (s.inv <= 0) {
        const crash = g.rocks.find((r) => Math.hypot(s.x - r.x, s.y - r.y) < r.r + 10);
        if (crash) {
          g.lives--;
          if (g.lives <= 0) {
            endRun();
            return;
          }
          s.x = g.w / 2;
          s.y = g.h / 2;
          s.vx = 0;
          s.vy = 0;
          s.a = -Math.PI / 2;
          s.inv = 2.4;
        }
      }
      if (g.rocks.length === 0 && !g._waveT) {
        g._waveT = setTimeout(() => {
          g._waveT = null;
          g.wave++;
          if (g.mode === 'playing') spawnWave(3 + g.wave);
        }, 1000);
      }
      updHud();
    };
    const drawGame = () => {
      const c = g.ctx;
      c.clearRect(0, 0, g.w, g.h);
      c.fillStyle = '#050506';
      c.fillRect(0, 0, g.w, g.h);
      g.stars.forEach((st) => {
        c.globalAlpha = st.a;
        c.fillStyle = '#f2f2f0';
        c.fillRect(st.x * g.w, st.y * g.h, st.r, st.r);
      });
      c.globalAlpha = 1;
      c.strokeStyle = 'rgba(242,242,240,.9)';
      c.lineWidth = 1.4;
      g.rocks.forEach((r) => {
        c.save();
        c.translate(r.x, r.y);
        c.rotate(r.rot);
        c.beginPath();
        r.verts.forEach((v, i) => {
          const a = (i / r.verts.length) * Math.PI * 2;
          const px = Math.cos(a) * r.r * v;
          const py = Math.sin(a) * r.r * v;
          i === 0 ? c.moveTo(px, py) : c.lineTo(px, py);
        });
        c.closePath();
        c.stroke();
        c.restore();
      });
      c.fillStyle = '#f2f2f0';
      g.bullets.forEach((b) => {
        c.beginPath();
        c.arc(b.x, b.y, 2, 0, Math.PI * 2);
        c.fill();
      });
      const s = g.ship;
      if (s && g.mode !== 'idle') {
        const blink = s.inv > 0 && Math.floor(performance.now() / 120) % 2 === 0;
        if (!blink) {
          c.save();
          c.translate(s.x, s.y);
          c.rotate(s.a);
          c.beginPath();
          c.moveTo(15, 0);
          c.lineTo(-11, 9);
          c.lineTo(-7, 0);
          c.lineTo(-11, -9);
          c.closePath();
          c.stroke();
          if (s.thrust && Math.random() > 0.35) {
            c.beginPath();
            c.moveTo(-9, 4);
            c.lineTo(-17 - Math.random() * 6, 0);
            c.lineTo(-9, -4);
            c.stroke();
          }
          c.restore();
        }
      }
    };
    const startRun = () => {
      g.mode = 'playing';
      g.startEl.style.display = 'none';
      g.overEl.style.display = 'none';
      g.score = 0;
      g.lives = 3;
      g.wave = 1;
      g.bullets = [];
      g.rocks = [];
      g.ship = { x: g.w / 2, y: g.h / 2, a: -Math.PI / 2, vx: 0, vy: 0, cool: 0, inv: 2 };
      spawnWave(4);
      updHud();
    };
    const openGame = () => {
      if (g.open) return;
      g.open = true;
      g.overlay.style.display = 'block';
      document.body.style.overflow = 'hidden';
      sizeCanvas();
      if (!g._welcomed) {
        g._welcomed = true;
        showToast('ACHIEVEMENT ✦ INSERT COIN');
      }
      g.mode = 'idle';
      g.startEl.style.display = 'flex';
      g.overEl.style.display = 'none';
      g.last = performance.now();
      const loop = (t) => {
        if (!g.open) return;
        const dt = Math.min(0.033, (t - g.last) / 1000);
        g.last = t;
        if (g.mode === 'playing') updateGame(dt);
        drawGame();
        g.raf = requestAnimationFrame(loop);
      };
      g.raf = requestAnimationFrame(loop);
    };
    const closeGame = () => {
      g.open = false;
      cancelAnimationFrame(g.raf);
      g.overlay.style.display = 'none';
      document.body.style.overflow = '';
      g.keys = {};
    };

    on(stage.querySelector('[data-gm-toggle]'), 'click', openGame);
    on(stage.querySelector('[data-gm-exit]'), 'click', closeGame);
    on(g.startEl, 'click', startRun);
    on(g.overEl, 'click', startRun);
    on(window, 'resize', () => {
      if (g.open) sizeCanvas();
    });
    on(window, 'keydown', (e) => {
      if (!g.open) {
        if ((e.key === 'g' || e.key === 'G') && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          openGame();
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeGame();
        return;
      }
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) e.preventDefault();
      if (e.key === 'Enter' && g.mode !== 'playing') {
        e.preventDefault();
        startRun();
        return;
      }
      g.keys[e.key] = true;
    });
    on(window, 'keyup', (e) => {
      g.keys[e.key] = false;
    });
    const tcKeys = { left: 'ArrowLeft', right: 'ArrowRight', thrust: 'ArrowUp', fire: ' ' };
    stage.querySelectorAll('[data-tc]').forEach((btn) => {
      const key = tcKeys[btn.getAttribute('data-tc')];
      const press = (e) => {
        e.preventDefault();
        g.keys[key] = true;
        btn.style.background = 'rgba(255,255,255,.18)';
      };
      const release = () => {
        g.keys[key] = false;
        btn.style.background = '';
      };
      on(btn, 'pointerdown', press);
      on(btn, 'pointerup', release);
      on(btn, 'pointercancel', release);
      on(btn, 'pointerleave', release);
      on(btn, 'contextmenu', (e) => e.preventDefault());
    });
    cleanups.push(() => {
      cancelAnimationFrame(g.raf);
      clearTimeout(g._waveT);
      document.body.style.overflow = '';
    });

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return (
    <div
      ref={stageRef}
      style={{ background: '#08080a', color: '#f2f2f0', minHeight: '100vh' }}
    >
      {/* HUD */}
      <div
        className="mg-hud"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 40,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          padding: '13px 28px',
          background: 'rgba(8,8,10,.72)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,.1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            data-egg
            title="Click me"
            style={{
              width: 38,
              height: 38,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255,255,255,.35)',
              borderRadius: 10,
              fontFamily: serif,
              fontStyle: 'italic',
              fontSize: 17,
              userSelect: 'none',
            }}
          >
            MG
          </div>
          <div
            className="mg-hud-player"
            style={{
              fontFamily: mono,
              fontSize: 10,
              letterSpacing: '.22em',
              color: 'rgba(242,242,240,.55)',
            }}
          >
            PLAYER ONE
          </div>
        </div>
        <div
          className="mg-hud-xp"
          style={{
            flex: 1,
            maxWidth: 280,
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.18em', color: 'rgba(242,242,240,.5)' }}>
            XP
          </div>
          <div
            style={{
              flex: 1,
              height: 3,
              background: 'rgba(255,255,255,.14)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              data-fill
              style={{ width: '0%', height: '100%', background: '#f5f5f3', boxShadow: '0 0 8px rgba(255,255,255,.7)' }}
            />
          </div>
        </div>
        <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: '.14em', color: '#f2f2f0' }}>
          LVL <span data-lvl>01</span>
          <span style={{ color: 'rgba(242,242,240,.4)' }}> / 05</span>
        </div>
        <div
          data-pct
          className="mg-hud-pct"
          style={{
            fontFamily: mono,
            fontSize: 10.5,
            letterSpacing: '.14em',
            color: 'rgba(242,242,240,.5)',
            minWidth: 36,
            textAlign: 'right',
          }}
        >
          00%
        </div>
        <button
          data-gm-toggle
          className="mg-btn-gm"
          style={{
            fontFamily: mono,
            fontSize: 9.5,
            letterSpacing: '.18em',
            color: '#f2f2f0',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,.35)',
            borderRadius: 999,
            padding: '8px 16px',
            cursor: 'pointer',
            transition: 'all .3s',
          }}
        >
          ▶ GAME MODE
        </button>
      </div>

      {/* LEVEL 01 — SPAWN */}
      <section
        className="mg-hero"
        style={{
          minHeight: 'calc(100vh - 65px)',
          display: 'flex',
          alignItems: 'center',
          gap: '5vw',
          padding: '60px 7vw',
          position: 'relative',
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.035) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.035) 1px,transparent 1px)',
          backgroundSize: '64px 64px',
          boxSizing: 'border-box',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: mono,
              fontSize: 11,
              letterSpacing: '.28em',
              color: 'rgba(242,242,240,.5)',
              marginBottom: 30,
            }}
          >
            LEVEL 01 — SPAWN
          </div>
          <h1
            style={{
              margin: 0,
              fontWeight: 400,
              fontFamily: serif,
              fontSize: 'clamp(64px,10vw,150px)',
              lineHeight: 0.9,
              letterSpacing: '-.01em',
            }}
          >
            Mauricio
            <br />
            <span style={{ fontStyle: 'italic', color: 'rgba(242,242,240,.92)' }}>Guevara</span>
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 36 }}>
            <div style={{ width: 64, height: 1, background: 'rgba(255,255,255,.4)' }} />
            <div
              style={{
                fontFamily: serif,
                fontStyle: 'italic',
                fontSize: 'clamp(19px,2.2vw,27px)',
                color: 'rgba(242,242,240,.65)',
              }}
            >
              New to IT. Not to hard work.
            </div>
          </div>
        </div>
        <div data-portrait className="mg-portrait" style={{ flex: 'none', position: 'relative', width: 'clamp(240px,26vw,360px)' }}>
          <div
            data-portrait-frame
            data-anim
            style={{
              position: 'relative',
              borderRadius: 20,
              padding: 1,
              background: shineBgStrong,
              backgroundSize: '200% 100%',
              animation: 'dvshine 6s linear infinite',
            }}
          >
            <div style={{ position: 'relative', borderRadius: 19, overflow: 'hidden', background: '#0d0d10' }}>
              <div
                data-portrait-media
                style={{ width: '100%', aspectRatio: '4 / 5', position: 'relative', transition: 'filter .5s' }}
              >
                <img
                  src={PORTRAIT_SRC}
                  alt="Mauricio Guevara"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <canvas
                  data-chrome-canvas
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    opacity: 0,
                    transition: 'opacity .5s',
                  }}
                />
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 12,
              fontFamily: mono,
              fontSize: 9,
              letterSpacing: '.2em',
              color: 'rgba(242,242,240,.45)',
            }}
          >
            <span>PLAYER_01.RAW</span>
            <span data-chrome-hint>HOVER — CHROME MODE</span>
          </div>
        </div>
        <div
          data-anim
          style={{
            position: 'absolute',
            bottom: 34,
            left: '7vw',
            fontFamily: mono,
            fontSize: 10,
            letterSpacing: '.26em',
            color: 'rgba(242,242,240,.6)',
            animation: 'dvpulse 2.4s ease-in-out infinite',
          }}
        >
          SCROLL TO START ▾
        </div>
        <div
          className="mg-hero-press"
          style={{
            position: 'absolute',
            bottom: 34,
            right: '7vw',
            fontFamily: mono,
            fontSize: 10,
            letterSpacing: '.22em',
            color: 'rgba(242,242,240,.45)',
          }}
        >
          PRESS G — ASTEROIDS (1979)
        </div>
      </section>

      {/* LEVEL 02 — PROJECTS */}
      <section style={{ padding: '70px 7vw 90px', maxWidth: 1060, margin: '0 auto', boxSizing: 'border-box' }}>
        <div
          data-reveal
          data-toast-msg="+25 XP — LEVEL 02 UNLOCKED"
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(255,255,255,.14)',
            paddingTop: 20,
            marginBottom: 30,
          }}
        >
          <div>
            <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '.24em', color: 'rgba(242,242,240,.5)', marginBottom: 10 }}>
              LEVEL 02
            </div>
            <h2 style={{ margin: 0, fontWeight: 400, fontFamily: serif, fontSize: 'clamp(38px,4.6vw,58px)', lineHeight: 1 }}>
              Projects
            </h2>
          </div>
          <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: '.18em', color: 'rgba(242,242,240,.5)' }}>+25 XP</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 46 }}>
          {groups.map((grp) => (
            <div key={grp.zone}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: '.24em', color: 'rgba(242,242,240,.65)' }}>
                  WORLD {grp.zone} — {grp.label}
                </div>
                <div style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: '.18em', color: 'rgba(242,242,240,.4)' }}>
                  {grp.count}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {grp.projects.map((proj) => {
                  const open = !!openLogs[proj.id];
                  return (
                    <div
                      key={proj.id}
                      data-reveal
                      data-delay={proj.delay}
                      data-anim
                      style={{
                        borderRadius: 18,
                        padding: 1,
                        background: shineBg,
                        backgroundSize: '200% 100%',
                        animation: 'dvshine 6s linear infinite',
                      }}
                    >
                      <div className="mg-card-inner" style={{ borderRadius: 17, background: '#0d0d10', padding: '30px 34px' }}>
                        <div onClick={() => toggleLog(proj.id)} style={{ cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14 }}>
                            <div style={{ fontFamily: serif, fontSize: 'clamp(26px,3vw,36px)' }}>{proj.name}</div>
                            <div style={{ fontFamily: mono, fontSize: 11, color: 'rgba(242,242,240,.5)' }}>{proj.year}</div>
                          </div>
                          <div style={{ fontSize: 14.5, lineHeight: 1.6, color: 'rgba(242,242,240,.6)', marginTop: 10, maxWidth: 600 }}>
                            {proj.desc}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 20, flexWrap: 'wrap' }}>
                          <button
                            onClick={() => toggleLog(proj.id)}
                            className="mg-log"
                            style={{
                              fontFamily: mono,
                              fontSize: 9.5,
                              letterSpacing: '.16em',
                              color: '#f2f2f0',
                              background: 'rgba(255,255,255,.08)',
                              border: '1px solid rgba(255,255,255,.25)',
                              borderRadius: 999,
                              padding: '6px 14px',
                              cursor: 'pointer',
                              transition: 'all .3s',
                            }}
                          >
                            {open ? 'CLOSE LOG ▴' : 'MISSION LOG ▾'}
                          </button>
                          {proj.tags.map((tag) => (
                            <div
                              key={tag}
                              style={{
                                fontFamily: mono,
                                fontSize: 9.5,
                                letterSpacing: '.14em',
                                color: 'rgba(242,242,240,.55)',
                                border: '1px solid rgba(255,255,255,.18)',
                                borderRadius: 999,
                                padding: '5px 12px',
                              }}
                            >
                              {tag}
                            </div>
                          ))}
                          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 9 }}>
                            {proj.video ? (
                              <a
                                href={proj.video}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mg-watch"
                                style={{
                                  textDecoration: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 7,
                                  fontFamily: mono,
                                  fontSize: 9.5,
                                  letterSpacing: '.16em',
                                  color: 'rgba(242,242,240,.8)',
                                  border: '1px solid rgba(255,255,255,.25)',
                                  borderRadius: 999,
                                  padding: '6px 14px',
                                  transition: 'all .3s',
                                }}
                              >
                                ▶ WATCH
                              </a>
                            ) : null}
                            <a
                              href={proj.github}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mg-see"
                              style={{
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                                fontFamily: mono,
                                fontSize: 9.5,
                                letterSpacing: '.16em',
                                color: '#0a0a0c',
                                background: '#f2f2f0',
                                border: '1px solid #f2f2f0',
                                borderRadius: 999,
                                padding: '6px 14px',
                                transition: 'all .3s',
                              }}
                            >
                              <GithubMark />
                              SEE PROJECT
                            </a>
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateRows: open ? '1fr' : '0fr',
                            transition: 'grid-template-rows .55s cubic-bezier(.22,.7,.25,1)',
                          }}
                        >
                          <div style={{ overflow: 'hidden', minHeight: 0 }}>
                            <div
                              style={{
                                borderTop: '1px solid rgba(255,255,255,.12)',
                                marginTop: 24,
                                paddingTop: 24,
                                opacity: open ? 1 : 0,
                                transition: 'opacity .5s .12s',
                              }}
                            >
                              <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.24em', color: 'rgba(242,242,240,.45)', marginBottom: 10 }}>
                                MISSION BRIEFING
                              </div>
                              <div style={{ fontSize: 14, lineHeight: 1.7, color: 'rgba(242,242,240,.7)', maxWidth: 600 }}>
                                {proj.brief}
                              </div>
                              <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.24em', color: 'rgba(242,242,240,.45)', margin: '24px 0 6px' }}>
                                WALKTHROUGH
                              </div>
                              {proj.steps.map((step, j) => (
                                <div
                                  key={j}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '34px 1fr',
                                    gap: 12,
                                    padding: '8px 0',
                                    borderBottom: '1px solid rgba(255,255,255,.06)',
                                  }}
                                >
                                  <div style={{ fontFamily: mono, fontSize: 10, color: 'rgba(242,242,240,.4)', paddingTop: 2 }}>
                                    {String(j + 1).padStart(2, '0')}
                                  </div>
                                  <div style={{ fontSize: 13.5, lineHeight: 1.6, color: 'rgba(242,242,240,.65)' }}>{step}</div>
                                </div>
                              ))}
                              {proj.bugs.length > 0 ? (
                                <>
                                  <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.24em', color: 'rgba(242,242,240,.45)', margin: '24px 0 6px' }}>
                                    BOSS FIGHTS — TROUBLESHOOTING
                                  </div>
                                  {proj.bugs.map((bug, j) => (
                                    <div key={j} style={{ padding: '9px 0' }}>
                                      <div style={{ fontSize: 13.5, lineHeight: 1.6, color: 'rgba(242,242,240,.85)' }}>✕ {bug.p}</div>
                                      <div style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(242,242,240,.55)', marginTop: 4 }}>
                                        ▸ FIX — {bug.f}
                                      </div>
                                    </div>
                                  ))}
                                </>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <a
            data-reveal
            data-delay="260"
            href={githubProfile}
            target="_blank"
            rel="noopener noreferrer"
            className="mg-more"
            style={{
              textDecoration: 'none',
              display: 'block',
              border: '1px dashed rgba(255,255,255,.22)',
              borderRadius: 18,
              padding: 28,
              textAlign: 'center',
              fontFamily: mono,
              fontSize: 10.5,
              letterSpacing: '.22em',
              color: 'rgba(242,242,240,.4)',
              transition: 'all .3s',
            }}
          >
            MORE SHIPPING SOON — FOLLOW ON GITHUB ▸
          </a>
        </div>
      </section>

      {/* LEVEL 03 — INVENTORY */}
      <section style={{ padding: '20px 0 90px' }}>
        <div
          data-reveal
          data-toast-msg="+25 XP — LEVEL 03 UNLOCKED"
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(255,255,255,.14)',
            paddingTop: 20,
            margin: '0 7vw 32px',
          }}
        >
          <div>
            <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '.24em', color: 'rgba(242,242,240,.5)', marginBottom: 10 }}>
              LEVEL 03
            </div>
            <h2 style={{ margin: 0, fontWeight: 400, fontFamily: serif, fontSize: 'clamp(38px,4.6vw,58px)', lineHeight: 1 }}>
              Inventory
            </h2>
          </div>
          <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: '.18em', color: 'rgba(242,242,240,.5)' }}>10 ITEMS</div>
        </div>
        <div
          style={{
            overflow: 'hidden',
            borderTop: '1px solid rgba(255,255,255,.1)',
            borderBottom: '1px solid rgba(255,255,255,.1)',
            padding: '26px 0',
          }}
        >
          <div data-anim style={{ display: 'flex', width: 'max-content', animation: 'dvmarq 30s linear infinite' }}>
            <SkillMarqueeGroup />
            <SkillMarqueeGroup />
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            marginTop: 18,
            fontFamily: mono,
            fontSize: 9.5,
            letterSpacing: '.24em',
            color: 'rgba(242,242,240,.35)',
          }}
        >
          MORE LOADING — SURRY CC, CLASS OF 2027
        </div>
      </section>

      {/* LEVEL 04 — BACKSTORY */}
      <section style={{ padding: '20px 7vw 90px', maxWidth: 1060, margin: '0 auto', boxSizing: 'border-box' }}>
        <div
          data-reveal
          data-toast-msg="+25 XP — LEVEL 04 UNLOCKED"
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(255,255,255,.14)',
            paddingTop: 20,
            marginBottom: 10,
          }}
        >
          <div>
            <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '.24em', color: 'rgba(242,242,240,.5)', marginBottom: 10 }}>
              LEVEL 04
            </div>
            <h2 style={{ margin: 0, fontWeight: 400, fontFamily: serif, fontSize: 'clamp(38px,4.6vw,58px)', lineHeight: 1 }}>
              Backstory
            </h2>
          </div>
          <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: '.18em', color: 'rgba(242,242,240,.5)' }}>2016 — NOW</div>
        </div>
        <div
          data-reveal
          className="mg-trow"
          style={{
            display: 'grid',
            gridTemplateColumns: '130px 1fr',
            gap: 20,
            padding: '24px 0',
            borderBottom: '1px solid rgba(255,255,255,.1)',
          }}
        >
          <div style={{ fontFamily: mono, fontSize: 11, color: 'rgba(242,242,240,.5)', paddingTop: 8 }}>2025 — 2027</div>
          <div>
            <div style={{ fontFamily: serif, fontSize: 'clamp(24px,2.6vw,32px)' }}>Surry Community College</div>
            <div style={{ fontSize: 14, color: 'rgba(242,242,240,.55)', marginTop: 6, lineHeight: 1.6 }}>
              Associate's degree — programming languages, networking, security.
            </div>
          </div>
        </div>
        <div
          data-reveal
          data-delay="120"
          className="mg-trow"
          style={{
            display: 'grid',
            gridTemplateColumns: '130px 1fr',
            gap: 20,
            padding: '24px 0',
            borderBottom: '1px solid rgba(255,255,255,.1)',
          }}
        >
          <div style={{ fontFamily: mono, fontSize: 11, color: 'rgba(242,242,240,.5)', paddingTop: 8 }}>2020 — 2025</div>
          <div>
            <div style={{ fontFamily: serif, fontSize: 'clamp(24px,2.6vw,32px)' }}>
              Machine Operator III{' '}
              <span style={{ fontStyle: 'italic', color: 'rgba(242,242,240,.55)' }}>— Frontline Steel Structures</span>
            </div>
            <div style={{ fontSize: 14, color: 'rgba(242,242,240,.55)', marginTop: 6, lineHeight: 1.6 }}>
              Five years keeping heavy systems running — troubleshooting machines, leading builds, documenting everything.
              Zero patience for downtime.
            </div>
          </div>
        </div>
        <div data-reveal data-delay="200" className="mg-trow" style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 20, padding: '24px 0' }}>
          <div style={{ fontFamily: mono, fontSize: 11, color: 'rgba(242,242,240,.5)', paddingTop: 8 }}>2016 — 2020</div>
          <div>
            <div style={{ fontFamily: serif, fontSize: 'clamp(24px,2.6vw,32px)' }}>North Surry High</div>
            <div style={{ fontSize: 14, color: 'rgba(242,242,240,.55)', marginTop: 6, lineHeight: 1.6 }}>
              Advanced classes, then straight into the workforce.
            </div>
          </div>
        </div>
      </section>

      {/* LEVEL 05 — THE PLAYER */}
      <section style={{ padding: '20px 7vw 60px', maxWidth: 1060, margin: '0 auto', boxSizing: 'border-box' }}>
        <div
          data-reveal
          data-toast-msg="+25 XP — FINAL LEVEL"
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(255,255,255,.14)',
            paddingTop: 20,
            marginBottom: 26,
          }}
        >
          <div>
            <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '.24em', color: 'rgba(242,242,240,.5)', marginBottom: 10 }}>
              LEVEL 05
            </div>
            <h2 style={{ margin: 0, fontWeight: 400, fontFamily: serif, fontSize: 'clamp(38px,4.6vw,58px)', lineHeight: 1 }}>
              The Player
            </h2>
          </div>
          <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: '.18em', color: 'rgba(242,242,240,.5)' }}>ABOUT</div>
        </div>
        <div data-reveal style={{ fontSize: 16, lineHeight: 1.8, color: 'rgba(242,242,240,.72)', maxWidth: 640 }}>
          <span style={{ float: 'left', fontFamily: serif, fontSize: 74, lineHeight: 0.76, padding: '7px 14px 0 0' }}>F</span>
          ive years as a machine operator taught me how big systems fail — and how to be the one who fixes them. Now I study
          programming, networking, and security. AI-curious. Family-first. Allergic to unlabeled cables.
        </div>
        <div data-reveal data-delay="140" style={{ display: 'flex', gap: 14, marginTop: 36, flexWrap: 'wrap' }}>
          <a
            href={githubProfile}
            target="_blank"
            rel="noopener noreferrer"
            className="mg-email"
            style={{
              textDecoration: 'none',
              fontFamily: mono,
              fontSize: 10,
              letterSpacing: '.16em',
              border: '1px solid rgba(255,255,255,.25)',
              borderRadius: 999,
              padding: '9px 18px',
              transition: 'all .3s',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            <GithubMark />
            GITHUB — SAY HI ▸
          </a>
        </div>
        <div
          className="mg-footer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 14,
            marginTop: 70,
            paddingTop: 20,
            borderTop: '1px solid rgba(255,255,255,.1)',
          }}
        >
          <div style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: '.22em', color: 'rgba(242,242,240,.4)' }}>
            MG — 2026. THANKS FOR PLAYING.
          </div>
          <div style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: '.22em', color: 'rgba(242,242,240,.4)' }}>
            CONTINUE? PRESS G ▸
          </div>
        </div>
      </section>

      {/* PAGE TOAST */}
      <div
        data-toast
        style={{
          position: 'fixed',
          left: '50%',
          bottom: 26,
          transform: 'translateX(-50%) translateY(12px)',
          opacity: 0,
          transition: 'opacity .4s, transform .4s',
          pointerEvents: 'none',
          fontFamily: mono,
          fontWeight: 500,
          fontSize: 11.5,
          letterSpacing: '.08em',
          background: '#f2f2f0',
          color: '#0a0a0c',
          padding: '10px 18px',
          borderRadius: 999,
          whiteSpace: 'nowrap',
          zIndex: 70,
        }}
      />

      {/* GAME OVERLAY */}
      <div data-game-overlay style={{ position: 'fixed', inset: 0, zIndex: 60, background: '#050506', display: 'none' }}>
        <canvas data-game-canvas style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />
        <div
          className="mg-gm-bar"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '16px 26px',
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '.16em', color: '#f2f2f0' }}>
            SCORE <span data-gm-score>00000</span>
            <span style={{ color: 'rgba(242,242,240,.45)' }}>
              &nbsp;&nbsp;BEST <span data-gm-best>00000</span>
            </span>
          </div>
          <div className="mg-gm-title" style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 19, color: 'rgba(242,242,240,.85)' }}>
            Asteroids{' '}
            <span style={{ fontFamily: mono, fontStyle: 'normal', fontSize: 9, letterSpacing: '.22em', color: 'rgba(242,242,240,.45)' }}>
              — EST. 1979
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '.16em', color: '#f2f2f0' }}>
              <span style={{ color: 'rgba(242,242,240,.45)' }}>SHIPS</span> <span data-gm-lives>▲ ▲ ▲</span>
            </div>
            <button
              data-gm-exit
              className="mg-btn-gm"
              style={{
                pointerEvents: 'auto',
                fontFamily: mono,
                fontSize: 9.5,
                letterSpacing: '.18em',
                color: '#f2f2f0',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,.35)',
                borderRadius: 999,
                padding: '7px 14px',
                cursor: 'pointer',
                transition: 'all .3s',
              }}
            >
              ESC — EXIT
            </button>
          </div>
        </div>
        <div
          className="mg-touch"
          style={{
            position: 'absolute',
            left: 22,
            right: 22,
            bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', gap: 12 }}>
            <button data-tc="left" className="mg-pad" style={{ width: 62, height: 62 }}>
              ◀
            </button>
            <button data-tc="right" className="mg-pad" style={{ width: 62, height: 62 }}>
              ▶
            </button>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button data-tc="thrust" className="mg-pad" style={{ width: 62, height: 62 }}>
              ▲
            </button>
            <button data-tc="fire" className="mg-pad" style={{ width: 92, height: 62, fontSize: 9.5, letterSpacing: '.18em' }}>
              FIRE
            </button>
          </div>
        </div>
        <div
          data-gm-start
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            cursor: 'pointer',
            gap: 22,
          }}
        >
          <div style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 'clamp(54px,8vw,92px)', lineHeight: 1 }}>
            Asteroids<span style={{ color: 'rgba(242,242,240,.4)' }}>.</span>
          </div>
          <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: '.22em', color: 'rgba(242,242,240,.55)', lineHeight: 2.2 }}>
            <span className="mg-hint-kbd">
              ← → ROTATE&nbsp;&nbsp;·&nbsp;&nbsp;↑ THRUST&nbsp;&nbsp;·&nbsp;&nbsp;SPACE FIRE
            </span>
            <span className="mg-hint-touch">◀ ▶ ROTATE&nbsp;&nbsp;·&nbsp;&nbsp;▲ THRUST&nbsp;&nbsp;·&nbsp;&nbsp;HOLD FIRE</span>
            <br />
            SMALL ROCKS ARE WORTH MORE.
          </div>
          <div
            data-anim
            style={{ fontFamily: mono, fontSize: 11, letterSpacing: '.26em', color: '#f2f2f0', animation: 'dvpulse 2s ease-in-out infinite' }}
          >
            <span className="mg-hint-kbd">PRESS ENTER — OR CLICK — TO START</span>
            <span className="mg-hint-touch">TAP ANYWHERE TO START</span>
          </div>
        </div>
        <div
          data-gm-over
          style={{
            position: 'absolute',
            inset: 0,
            display: 'none',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            cursor: 'pointer',
            gap: 20,
          }}
        >
          <div style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 'clamp(54px,8vw,92px)', lineHeight: 1 }}>
            Game over<span style={{ color: 'rgba(242,242,240,.4)' }}>.</span>
          </div>
          <div data-gm-final style={{ fontFamily: mono, fontSize: 11, letterSpacing: '.22em', color: 'rgba(242,242,240,.6)' }}>
            SCORE 00000 — BEST 00000
          </div>
          <div
            data-anim
            style={{ fontFamily: mono, fontSize: 11, letterSpacing: '.26em', color: '#f2f2f0', animation: 'dvpulse 2s ease-in-out infinite' }}
          >
            <span className="mg-hint-kbd">ENTER — RETRY&nbsp;&nbsp;·&nbsp;&nbsp;ESC — EXIT</span>
            <span className="mg-hint-touch">TAP — RETRY&nbsp;&nbsp;·&nbsp;&nbsp;EXIT ABOVE</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
